import React from 'react';
import { View, TouchableOpacity, Text, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNotifications } from '@/hooks/useNotifications';
import { colors } from '@/design-system/theme/colors';

interface NotificationBellProps {
  onPress: () => void;
}

/**
 * NotificationBell - Simple bell icon with unread count badge
 *
 * Features:
 * - Large touch target (44x44px) for accessibility
 * - Red badge with unread count (max "9+")
 * - Ultra-simple design for technically challenged users
 */
export function NotificationBell({ onPress }: NotificationBellProps) {
  const { unreadCount } = useNotifications();

  const displayCount = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      style={styles.container}
      accessibilityLabel={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      accessibilityRole="button"
    >
      <MaterialIcons
        name="notifications"
        size={24}
        color={colors.gray[700]}
      />

      {unreadCount > 0 && (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>{displayCount}</Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: 6,
    right: 6,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
    lineHeight: 12,
  },
});

export default NotificationBell;
